// services/optimizedSearchService.js

import {
    patientAPI,
    doctorAPI,
    userAPI,
    branchAPI,
    scanAPI,
    technicianAPI,
    doctorSpecializationAPI,
    scanCategoryAPI,
    expenseCategoryAPI,
    stockAPI,
} from './api';
import CentralStockAPI from '../services/centralStockAPI';
import representativeService from './representativeService';
const DEFAULT_SEARCH_OPTIONS = {
    limit: 25,
    page: 1,
};

const performSearch = async (apiCall, searchTerm, options = {}, searchParamName = 'search') => {
    try {
        const params = {
            ...DEFAULT_SEARCH_OPTIONS,
            ...options,
        };

        if (searchTerm && searchTerm.trim()) {
            params[searchParamName] = searchTerm.trim();
        }

        const response = await apiCall(params);
        return response;
    } catch (error) {
        console.error(`Search error for ${options.type || 'entity'}:`, error);
        return { data: { data: [], pagination: { hasMore: false, total: 0 } } };
    }
};

//   FIX: Always include _id in select fields
export const searchPatients = (searchTerm, options = {}) => {
    return performSearch(patientAPI.getAll, searchTerm, {
        select: '_id,name,phoneNumber,gender,age', //   Added _id
        ...options
    });
};

export const searchDoctors = (searchTerm, options = {}) => {
    return performSearch(doctorAPI.getAll, searchTerm, {
        // Use space-separated to match backend field splitting convention
        // 'clinics' brings the full clinics subdoc array including representative refs
        fields: '_id,name,specialization,representative,clinics',
        ...options
    });
};

export const searchRadiologists = (searchTerm, options = {}) => {
    return performSearch(userAPI.getRadiologists, searchTerm, {
        fields: '_id,name,email,branch,username', //   Added _id and username
        ...options
    });
};

export const searchBranches = (searchTerm, options = {}) => {
    return performSearch(branchAPI.getAll, searchTerm, {
        select: '_id,name,address,isActive', //   Added _id
        ...options
    });
};

export const searchScans = (searchTerm, options = {}) => {
    return performSearch(scanAPI.getAll, searchTerm, {
        select: '_id,name,category,basePrice,category,branchPricing,items,actualCost', //   Added _id
        ...options
    }, 'name');
};

export const searchTechnicians = (searchTerm, options = {}) => {
    return performSearch(technicianAPI.getAll, searchTerm, {
        select: '_id,name,shift,department', //   Added _id
        ...options
    });
};

export const searchRepresentatives = (searchTerm, options = {}) => {
    return performSearch(representativeService.getAllRepresentatives, searchTerm, {
        select: '_id,name,phoneNumber,email', //   Added _id
        ...options
    });
};

export const searchSpecializations = (searchTerm, options = {}) => {
    return performSearch(doctorSpecializationAPI.getAll, searchTerm, {
        select: '_id,name,description', //   Added _id
        ...options
    });
};

export const searchCategories = (searchTerm, options = {}) => {
    return performSearch(scanCategoryAPI.getAll, searchTerm, {
        select: '_id,name,description,isActive', //   Added _id
        ...options
    });
};

/**
 * Generic search function that can search any entity
 * @param {string} entityType - Type of entity to search
 * @param {string} searchTerm - Search term
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} - API response
 */
export const searchEntity = (entityType, searchTerm, options = {}) => {
    const searchFunctions = {
        patients: searchPatients,
        doctors: searchDoctors,
        radiologists: searchRadiologists,
        branches: searchBranches,
        scans: searchScans,
        technicians: searchTechnicians,
        representatives: searchRepresentatives,
        specializations: searchSpecializations,
        categories: searchCategories,
    };

    const searchFn = searchFunctions[entityType];

    if (!searchFn) {
        console.error(`Unknown entity type: ${entityType}`);
        return Promise.resolve({ data: { data: [], pagination: { hasMore: false, total: 0 } } });
    }

    return searchFn(searchTerm, options);
};

export const searchExpenseCategories = (searchTerm, options = {}) => {
    return performSearch(expenseCategoryAPI.getAll, searchTerm, {
        select: '_id,name,description',
        ...options
    });
};

export const searchUsers = (searchTerm, options = {}) => {
    return performSearch(userAPI.getAll, searchTerm, {
        select: '_id,name,username',
        ...options
    });
};

export const searchStockItems = (searchTerm, options = {}) => {
    // Same endpoint the parent page uses to preload stock.
    // No `select`: that call works without it, and I don't know whether this endpoint supports it.
    return performSearch(stockAPI.getAll, searchTerm, { ...options }, 'search');
};
export const searchCentralStockItems = (searchTerm, options = {}) => {
    // Same endpoint the parent page uses to preload stock.
    // No `select`: that call works without it, and I don't know whether this endpoint supports it.
    return performSearch(CentralStockAPI.getAll, searchTerm, { ...options }, 'search');
};



export default {
    searchPatients,
    searchDoctors,
    searchRadiologists,
    searchBranches,
    searchScans,
    searchTechnicians,
    searchRepresentatives,
    searchSpecializations,
    searchCategories,
    searchEntity,
    searchExpenseCategories,
    searchUsers,
    searchStockItems
};